/**
 * Add Favorite Location Inline Component
 *
 * Simplified inline component for adding a single favorite location during profile setup (Step 1)
 * This is a streamlined version without the modal wrapper, designed for embedding in forms
 */

import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FaMapMarkerAlt } from 'react-icons/fa';
import Spinner from '../common/Spinner';
import { Checkbox1 } from '../Templates';
import './AddFavoriteLocationInline.css';

const AddFavoriteLocationInline = ({
  onLocationChange,
  initialLocation = null,
  disabled = false,
  showLabel = true,
  minimal = false
}) => {
  const { t } = useTranslation('common');
  const addressInputRef = useRef(null);
  const [googleMapsLoaded, setGoogleMapsLoaded] = useState(false);
  const [locationLoading, setLocationLoading] = useState(false);

  const [formData, setFormData] = useState({
    label: initialLocation?.label || '',
    address: initialLocation?.address || '',
    city: initialLocation?.city || '',
    latitude: initialLocation?.latitude || null,
    longitude: initialLocation?.longitude || null,
    place_id: initialLocation?.place_id || null,
    radius_km: initialLocation?.radius_km || 10,
    notify_for_rides: initialLocation?.notify_for_rides !== false,
    notify_for_packages: initialLocation?.notify_for_packages !== false,
    match_start_point: initialLocation?.match_start_point !== false,
    match_end_point: initialLocation?.match_end_point !== false
  });

  // Reset form when initialLocation changes
  useEffect(() => {
    if (initialLocation) {
      setFormData({
        label: initialLocation.label || '',
        address: initialLocation.address || '',
        city: initialLocation.city || '',
        latitude: initialLocation.latitude || null,
        longitude: initialLocation.longitude || null,
        place_id: initialLocation.place_id || null,
        radius_km: initialLocation.radius_km || 10,
        notify_for_rides: initialLocation.notify_for_rides !== false,
        notify_for_packages: initialLocation.notify_for_packages !== false,
        match_start_point: initialLocation.match_start_point !== false,
        match_end_point: initialLocation.match_end_point !== false
      });
    }
  }, [initialLocation]);

  // Check if Google Maps is loaded
  useEffect(() => {
    const checkGoogleMaps = () => {
      if (window.google?.maps?.places) {
        setGoogleMapsLoaded(true);
        initializeAutocomplete();
      } else {
        setTimeout(checkGoogleMaps, 500);
      }
    };

    checkGoogleMaps();
  }, []);

  const initializeAutocomplete = () => {
    if (!addressInputRef.current || !window.google?.maps) return;

    // Prevent double initialization
    if (addressInputRef.current.hasAttribute('data-autocomplete-initialized')) {
      return;
    }

    const autocomplete = new window.google.maps.places.Autocomplete(
      addressInputRef.current,
      { types: ['geocode'] }
    );

    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      if (place.geometry) {
        const cityComponent = place.address_components?.find(
          c => c.types.includes('locality') || c.types.includes('administrative_area_level_2')
        );

        setFormData(prev => ({
          ...prev,
          address: place.formatted_address,
          city: cityComponent?.long_name || '',
          latitude: place.geometry.location.lat(),
          longitude: place.geometry.location.lng(),
          place_id: place.place_id
        }));
      }
    });

    addressInputRef.current.setAttribute('data-autocomplete-initialized', 'true');
  };

  const handleChange = (field, value) => {
    const updatedData = { ...formData, [field]: value };
    setFormData(updatedData);

    // Notify parent of the change
    if (onLocationChange) {
      onLocationChange(updatedData);
    }
  };

  const detectCurrentLocation = async () => {
    setLocationLoading(true);

    try {
      if (!navigator.geolocation) {
        console.error('Geolocation is not supported by this browser');
        return;
      }

      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(
          resolve,
          reject,
          {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 300000
          }
        );
      });

      const { latitude, longitude } = position.coords;

      if (window.google?.maps) {
        const geocoder = new window.google.maps.Geocoder();
        const latLng = new window.google.maps.LatLng(latitude, longitude);

        geocoder.geocode({ location: latLng }, (results, status) => {
          if (status === 'OK' && results[0]) {
            const place = results[0];

            // Extract city
            const cityComponent = place.address_components?.find(
              c => c.types.includes('locality') ||
                   c.types.includes('administrative_area_level_2') ||
                   c.types.includes('administrative_area_level_1')
            );

            const updatedData = {
              ...formData,
              address: place.formatted_address,
              city: cityComponent?.long_name || '',
              latitude: latitude,
              longitude: longitude,
              place_id: place.place_id
            };

            setFormData(updatedData);

            // Notify parent
            if (onLocationChange) {
              onLocationChange(updatedData);
            }

            console.log('✅ Location detected:', place.formatted_address);
          } else {
            console.error('Geocoding failed:', status);
          }
        });
      } else {
        console.error('Google Maps not loaded');
      }
    } catch (error) {
      console.error('Location detection error:', error);

      if (error.code === 1) {
        console.error('Location permission denied');
      } else if (error.code === 2) {
        console.error('Location unavailable');
      } else if (error.code === 3) {
        console.error('Location request timeout');
      }
    } finally {
      setLocationLoading(false);
    }
  };

  return (
    <div className="fav-loc-inline-container">
      {showLabel && (
        <div className="fav-loc-inline-header">
          <label className="fav-loc-inline-label">
            <FaMapMarkerAlt /> {t('settings.favorite_location')}
          </label>
          <p className="fav-loc-inline-help">
            {t('settings.favorite_location_profile_help') || 'Get email alerts when rides/packages match this location'}
          </p>
        </div>
      )}

      <div className="fav-loc-inline-form">
        {/* Location Name — hidden in minimal (onboarding) mode; auto-labeled from city */}
        {!minimal && (
          <div className="fav-loc-inline-field">
            <label className="fav-loc-field-label">
              {t('settings.location_name')}
            </label>
            <input
              type="text"
              className="fav-loc-inline-input"
              value={formData.label}
              onChange={(e) => handleChange('label', e.target.value)}
              placeholder={t('settings.location_name_placeholder') || 'e.g., Home, Work, Gym'}
              maxLength={100}
              disabled={disabled}
            />
          </div>
        )}

        {/* Address with Google Autocomplete */}
        <div className="fav-loc-inline-field">
          <label className="fav-loc-field-label">
            {t('settings.address')}
          </label>
          <div className="fav-loc-address-container">
            <input
              ref={addressInputRef}
              type="text"
              className="fav-loc-inline-input"
              value={formData.address}
              onChange={(e) => handleChange('address', e.target.value)}
              placeholder={t('settings.address_placeholder') || 'Start typing an address...'}
              disabled={!googleMapsLoaded || disabled || locationLoading}
            />
            <button
              type="button"
              className="fav-loc-detect-btn"
              onClick={detectCurrentLocation}
              disabled={!googleMapsLoaded || disabled || locationLoading}
              title={t('settings.detect_location') || 'Auto-detect location'}
            >
              {locationLoading ? (
                <Spinner size="small" showText={false} />
              ) : (
                '📍'
              )}
            </button>
          </div>
          {!googleMapsLoaded && (
            <div className="fav-loc-inline-help-text">
              <Spinner size="small" showText={false} /> {t('settings.loading_maps') || 'Loading Google Maps...'}
            </div>
          )}
        </div>

        {/* Advanced controls (radius + notify preferences) — hidden in minimal
            onboarding mode; they keep sensible defaults and can be tuned later
            in Settings → Location Alerts. */}
        {!minimal && (
          <>
            {/* Radius Slider */}
            <div className="fav-loc-inline-field">
              <label className="fav-loc-field-label">
                {t('settings.search_radius')}: <strong>{formData.radius_km}km</strong>
              </label>
              <input
                type="range"
                min="5"
                max="20"
                step="5"
                value={formData.radius_km}
                onChange={(e) => handleChange('radius_km', parseInt(e.target.value))}
                className="fav-loc-inline-slider"
                disabled={disabled}
              />
              <div className="fav-loc-slider-labels">
                <span>5km</span>
                <span>10km</span>
                <span>15km</span>
                <span>20km</span>
              </div>
            </div>

            {/* Match Points - Compact Layout */}
            <div className="fav-loc-inline-field">
              <label className="fav-loc-field-label">
                {t('settings.notify_when_posts')}
              </label>
              <div className="fav-loc-inline-checkbox-group">
                <Checkbox1
                  id="inline-match-start"
                  label={`🚀 ${t('settings.start_near') || 'Start nearby'}`}
                  checked={formData.match_start_point}
                  onChange={(e) => handleChange('match_start_point', e.target.checked)}
                  disabled={disabled}
                />
                <Checkbox1
                  id="inline-match-end"
                  label={`🏁 ${t('settings.end_near') || 'End nearby'}`}
                  checked={formData.match_end_point}
                  onChange={(e) => handleChange('match_end_point', e.target.checked)}
                  disabled={disabled}
                />
              </div>
            </div>

            {/* Notify For - Compact Layout */}
            <div className="fav-loc-inline-field">
              <label className="fav-loc-field-label">
                {t('settings.notify_me_about')}
              </label>
              <div className="fav-loc-inline-checkbox-group">
                <Checkbox1
                  id="inline-notify-rides"
                  label={`🚗 ${t('settings.rides') || 'Rides'}`}
                  checked={formData.notify_for_rides}
                  onChange={(e) => handleChange('notify_for_rides', e.target.checked)}
                  disabled={disabled}
                />
                <Checkbox1
                  id="inline-notify-packages"
                  label={`📦 ${t('settings.packages') || 'Packages'}`}
                  checked={formData.notify_for_packages}
                  onChange={(e) => handleChange('notify_for_packages', e.target.checked)}
                  disabled={disabled}
                />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AddFavoriteLocationInline;
